import React, { useEffect, useState } from 'react';
import './Modals.css';

function parseCapabilities(xmlText, layerName) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

  const targetName = layerName.includes(':') ? layerName.split(':')[1] : layerName;

  const allLayers = xmlDoc.querySelectorAll('Layer');
  let found = null;

  for (const l of allLayers) {
    const nameEl = l.querySelector(':scope > Name');
    if (!nameEl) continue;
    const name = nameEl.textContent.trim();
    if (name === layerName || name === targetName) {
      found = l;
      break;
    }
  }

  if (!found) return null;

  const get = (sel) => {
    const el = found.querySelector(sel);
    return el ? el.textContent.trim() : null;
  };

  const title = get('Title');
  const abstract = get('Abstract');

  // Keywords
  const kwEls = found.querySelectorAll('Keyword');
  const keywords = Array.from(kwEls).map((kw) => kw.textContent.trim()).filter(Boolean);

  // SRS list
  const srsEls = found.querySelectorAll('SRS, CRS');
  const srsList = Array.from(srsEls).map((s) => s.textContent.trim()).filter(Boolean);

  // Bounding box
  let bbox = null;
  const bboxEl =
    found.querySelector('BoundingBox[CRS="EPSG:4326"]') ||
    found.querySelector('BoundingBox[SRS="EPSG:4326"]') ||
    found.querySelector('LatLonBoundingBox') ||
    found.querySelector('EX_GeographicBoundingBox');

  if (bboxEl) {
    if (bboxEl.tagName === 'EX_GeographicBoundingBox') {
      bbox = {
        minx: bboxEl.querySelector('westBoundLongitude')?.textContent || '',
        miny: bboxEl.querySelector('southBoundLatitude')?.textContent || '',
        maxx: bboxEl.querySelector('eastBoundLongitude')?.textContent || '',
        maxy: bboxEl.querySelector('northBoundLatitude')?.textContent || '',
      };
    } else {
      bbox = {
        minx: bboxEl.getAttribute('minx') || '',
        miny: bboxEl.getAttribute('miny') || '',
        maxx: bboxEl.getAttribute('maxx') || '',
        maxy: bboxEl.getAttribute('maxy') || '',
      };
    }
  }

  return { title, abstract, keywords, srsList, bbox };
}

function MetaModal({ layer, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    const capUrl =
      layer.baseUrl +
      `?service=WMS&request=GetCapabilities&version=${layer.version}`;
    const proxyUrl = `/api/wms-proxy?url=${encodeURIComponent(capUrl)}`;

    let cancelled = false;

    setLoading(true);
    setError(null);

    fetch(proxyUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        const parsed = parseCapabilities(text, layer.layers);
        if (!parsed) {
          setError('Camada não encontrada no GetCapabilities.');
        } else {
          setMeta(parsed);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Erro ao buscar metadados.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [layer]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Metadados da camada">
        <div className="modal-header">
          <h2 className="modal-title">Metadados — {layer.name}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="modal-loading">
              <span className="modal-spinner" />
              Carregando metadados…
            </div>
          )}

          {error && !loading && (
            <div className="modal-error">
              <strong>Erro:</strong> {error}
            </div>
          )}

          {meta && !loading && (
            <div className="meta-content">
              <div className="meta-section">
                <h3 className="meta-section-title">Informações Gerais</h3>
                <table className="meta-table">
                  <tbody>
                    <tr>
                      <th>Título</th>
                      <td>{meta.title || '—'}</td>
                    </tr>
                    <tr>
                      <th>Nome técnico</th>
                      <td><code>{layer.layers}</code></td>
                    </tr>
                    <tr>
                      <th>Formato</th>
                      <td>{layer.format}</td>
                    </tr>
                    <tr>
                      <th>Versão WMS</th>
                      <td>{layer.version}</td>
                    </tr>
                    <tr>
                      <th>URL base</th>
                      <td><code style={{ wordBreak: 'break-all' }}>{layer.baseUrl}</code></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {meta.abstract && (
                <div className="meta-section">
                  <h3 className="meta-section-title">Descrição</h3>
                  <p className="meta-abstract">{meta.abstract}</p>
                </div>
              )}

              {meta.bbox && (
                <div className="meta-section">
                  <h3 className="meta-section-title">Extensão Geográfica (EPSG:4326)</h3>
                  <table className="meta-table">
                    <tbody>
                      <tr>
                        <th>Oeste (minX)</th>
                        <td>{meta.bbox.minx}</td>
                      </tr>
                      <tr>
                        <th>Sul (minY)</th>
                        <td>{meta.bbox.miny}</td>
                      </tr>
                      <tr>
                        <th>Leste (maxX)</th>
                        <td>{meta.bbox.maxx}</td>
                      </tr>
                      <tr>
                        <th>Norte (maxY)</th>
                        <td>{meta.bbox.maxy}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {meta.srsList.length > 0 && (
                <div className="meta-section">
                  <h3 className="meta-section-title">Sistemas de Referência (SRS/CRS)</h3>
                  <div className="meta-tags">
                    {meta.srsList.map((srs) => (
                      <span key={srs} className="meta-tag">{srs}</span>
                    ))}
                  </div>
                </div>
              )}

              {meta.keywords.length > 0 && (
                <div className="meta-section">
                  <h3 className="meta-section-title">Palavras-chave</h3>
                  <div className="meta-tags">
                    {meta.keywords.map((kw) => (
                      <span key={kw} className="meta-tag">{kw}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MetaModal;
